import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation, Trans } from 'react-i18next';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { toast } from 'react-toastify';
import { sessionService } from '../services/session.service';
import { runGates } from '../gating/gateEngine';
import { handleGateDecision } from '../gating/gateHandler';
import { buildGateContext } from '../gating/buildGateContext';
import { AuthGate, SubscriptionGate } from '../gating/gates';

const LandingPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation('landing');
  const navigate = useNavigate();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [delaying, setDelaying] = useState(false);
  const isActiveRef = useRef(true);

  const faqItems = useMemo(
    () => [
      {
        question: 'How do I dispute a parking ticket in Toronto?',
        answer:
          'You can dispute a Toronto parking ticket by submitting an appeal through the City of Toronto. DisputeBot helps you prepare the appeal letter and guides you through the submission process.',
      },
      {
        question: 'Can I appeal a parking ticket if I already paid it?',
        answer:
          'In some cases, yes. If you paid due to time pressure or confusion, you may still request a review. DisputeBot helps determine if your case qualifies.',
      },
      {
        question: 'What is the deadline to dispute a parking ticket in Toronto?',
        answer:
          'Deadlines vary depending on the ticket type. It’s important to act quickly. Our tool helps you understand your timeline.',
      },
      {
        question: 'What reasons can help reduce or cancel a parking ticket?',
        answer:
          'Common reasons include unclear signage, payment machine issues, medical or emergency situations, and first-time offenses.',
      },
    ],
    []
  );

  const faqJsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    }),
    [faqItems]
  );

  const delay = (ms: number) =>
    new Promise<void>((resolve) => {
      setTimeout(resolve, ms);
    });

  useEffect(() => {
    document.title = 'Dispute a Parking Ticket in Toronto AI+HUMAN expert | Parking Ticket Appeal Online';
    const description =
      'Got a parking ticket in Toronto? Dispute or appeal your parking ticket online in minutes. Step-by-step guidance, AI-powered appeal letters, and proven results.';
    let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = description;
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${window.location.origin}/`;
  }, []);

  useEffect(() => {
    isActiveRef.current = true;
    return () => {
      isActiveRef.current = false;
    };
  }, []);

  const runStartFlow = async (file?: File, useDelay: boolean = false) => {
    const returnUrl = '/upload';
    const ctx = buildGateContext({
      user,
      returnUrl,
      refreshBilling: () => sessionService.getBillingMe(true),
    });
    const decision = await runGates([AuthGate, SubscriptionGate], ctx);
    if (useDelay) {
      setDelaying(true);
      await delay(500);
    }
    if (!isActiveRef.current) return;
    const allowed = handleGateDecision(decision, {
      navigate,
      toast: (message) => toast.error(message),
      returnUrl,
    });
    if (!allowed) {
      setUploading(false);
      setDelaying(false);
      return;
    }
    if (file) {
      navigate('/upload', { state: { file } });
    } else {
      navigate('/upload');
    }
    setDelaying(false);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp', '.tiff'],
      'application/pdf': ['.pdf']
    },
    multiple: false,
    maxSize: 10 * 1024 * 1024, // 10MB
    onDrop: async (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        setUploadedFile(file);
        if (user) {
          setUploading(true);
          await runStartFlow(file, true);
        }
      }
    },
    onDropRejected: (fileRejections) => {
      const rejection = fileRejections[0];
      if (rejection.errors[0].code === 'file-too-large') {
        toast.error(t('upload.errors.tooLarge'));
      } else if (rejection.errors[0].code === 'file-invalid-type') {
        toast.error(t('upload.errors.invalidType'));
      }
    }
  });

  const handleStartDispute = async () => {
    if (uploadedFile) {
      setUploading(true);
      await runStartFlow(uploadedFile, true);
    } else {
      await runStartFlow(undefined, false);
    }
  };

  const stats = [
    { label: t('stats.successRate'), value: '78%', icon: '📊' },
    { label: t('stats.ticketsProcessed'), value: '2,500+', icon: '🎫' },
    { label: t('stats.moneySaved'), value: '$125K+', icon: '💰' },
    { label: t('stats.avgTime'), value: '5 min', icon: '⏱️' },
  ];

  const testimonials = [
    {
      name: 'Sarah Chen',
      location: 'Downtown Toronto',
      text: 'Got my $150 parking ticket dismissed! The AI-generated appeal was spot on.',
      rating: 5,
      saved: '$150'
    },
    {
      name: 'Mike Rodriguez',
      location: 'North York',
      text: 'Super easy process. Uploaded my ticket, got a professional appeal letter, and won!',
      rating: 5,
      saved: '$75'
    },
    {
      name: 'Jennifer Liu',
      location: 'Scarborough',
      text: 'Saved me hours of research and writing. The appeal was comprehensive and effective.',
      rating: 5,
      saved: '$200'
    }
  ];

  const howItWorksSteps = [
    { step: 1, title: t('how.steps.1.title'), description: t('how.steps.1.desc'), icon: '📸' },
    { step: 2, title: t('how.steps.2.title'), description: t('how.steps.2.desc'), icon: '🤖' },
    { step: 3, title: t('how.steps.3.title'), description: t('how.steps.3.desc'), icon: '📝' },
    { step: 4, title: t('how.steps.4.title'), description: t('how.steps.4.desc'), icon: '🎉' }
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-slate-800">
      <section className="relative overflow-hidden">
        <div className="absolute top-0 right-0 -mr-20 -mt-20 h-96 w-96 rounded-full bg-[#C6A87C]/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 h-96 w-96 rounded-full bg-slate-900/10 blur-3xl" />
        <div className="relative z-10 mx-auto max-w-7xl px-4 pb-20 pt-16 sm:px-6 lg:px-8 lg:pb-24 lg:pt-24">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-20 lg:items-center">
            <div className="space-y-8 text-center lg:text-left">
              <h1 className="font-display text-4xl font-bold leading-tight text-slate-900 sm:text-5xl lg:text-6xl">
                Dispute a Parking Ticket in Toronto{' '}
                <span className="relative inline-block text-[#C6A87C] italic">
                  AI+HUMAN expert
                  <svg
                    className="absolute bottom-1 left-0 h-3 w-full opacity-30"
                    viewBox="0 0 100 10"
                    preserveAspectRatio="none"
                  >
                    <path
                      d="M0 5 Q 50 10 100 5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </span>
              </h1>
              <div className="mx-auto max-w-xl rounded-xl border border-slate-200 bg-white/90 p-6 text-left shadow-sm lg:mx-0">
                <div className="mb-4 flex items-center gap-3 border-b border-slate-100 pb-3">
                  <span className="material-symbols-outlined text-[#C6A87C]">verified_user</span>
                  <span className="font-display text-base font-semibold text-slate-900">
                    AI + Human Expert Review
                  </span>
                </div>
                <div className="relative flex flex-col gap-6 sm:flex-row sm:gap-6">
                  <div className="absolute left-4 right-4 top-3 hidden h-0.5 bg-slate-100 sm:block" />
                  <div className="relative z-10 flex flex-1 flex-col items-center text-center sm:flex-row sm:items-start sm:text-left sm:gap-3">
                    <div className="flex h-8 w-8 min-w-8 items-center justify-center rounded-full bg-blue-50 text-sm font-semibold text-blue-700 shadow-sm aspect-square shrink-0">
                      1
                    </div>
                    <div>
                      <span className="block text-sm font-semibold text-slate-900">Analysis</span>
                      <span className="block text-xs text-slate-500">
                        We scan your ticket details instantly
                      </span>
                    </div>
                  </div>
                  <div className="relative z-10 flex flex-1 flex-col items-center text-center sm:flex-row sm:items-start sm:text-left sm:gap-3">
                    <div className="flex h-8 w-8 min-w-8 items-center justify-center rounded-full bg-purple-50 text-sm font-semibold text-purple-700 shadow-sm aspect-square shrink-0">
                      2
                    </div>
                    <div>
                      <span className="block text-sm font-semibold text-slate-900">AI Collect Info</span>
                      <span className="block text-xs text-slate-500">
                        We gather the key ticket facts for you
                      </span>
                    </div>
                  </div>
                  <div className="relative z-10 flex flex-1 flex-col items-center text-center sm:flex-row sm:items-start sm:text-left sm:gap-3">
                    <div className="flex h-8 w-8 min-w-8 items-center justify-center rounded-full bg-emerald-50 text-sm font-semibold text-emerald-700 shadow-sm aspect-square shrink-0">
                      3
                    </div>
                    <div>
                      <span className="block text-sm font-semibold text-slate-900">Review</span>
                      <span className="block text-xs text-slate-500">Expert checks for accuracy</span>
                    </div>
                  </div>
                  <div className="relative z-10 flex flex-1 flex-col items-center text-center sm:flex-row sm:items-start sm:text-left sm:gap-3">
                    <div className="flex h-8 w-8 min-w-8 items-center justify-center rounded-full bg-amber-50 text-sm font-semibold text-amber-700 shadow-sm aspect-square shrink-0">
                      4
                    </div>
                    <div>
                      <span className="block text-sm font-semibold text-slate-900">Submit</span>
                      <span className="block text-xs text-slate-500">Ready to send in minutes</span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
                  <span className="material-symbols-outlined text-[16px] text-[#C6A87C]">info</span>
                  Not a law firm. No legal advice provided.
                </div>
              </div>
              <div className="flex flex-col gap-4 sm:flex-row sm:justify-center lg:justify-start">
                <button
                  onClick={handleStartDispute}
                  disabled={uploading || delaying}
                  className="flex items-center justify-center gap-2 rounded-lg bg-[#C6A87C] px-8 py-4 text-sm font-medium text-white shadow-[0_0_15px_rgba(198,168,124,0.3)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#B09265] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {uploading || delaying ? <LoadingSpinner size="sm" color="white" /> : t('hero.ctaPrimary')}
                </button>
              </div>
              <div className="flex flex-col items-center gap-4 pt-4 sm:flex-row sm:justify-center lg:justify-start">
                <div className="flex -space-x-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-[#FDFBF7] bg-slate-200 text-xs">
                    S
                  </div>
                  <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-[#FDFBF7] bg-slate-300 text-xs">
                    M
                  </div>
                  <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-[#FDFBF7] bg-emerald-100 text-xs text-emerald-700">
                    J
                  </div>
                </div>
                <div className="text-sm text-slate-500">
                  <Trans
                    i18nKey="hero.disputedCount"
                    ns="landing"
                    values={{ value: '2,500+' }}
                    components={{ b: <span className="font-semibold text-slate-900" /> }}
                  />
                </div>
                <div className="flex items-center gap-1 text-[#C6A87C]">
                  {'★'.repeat(5)}
                  <span className="ml-1 text-sm text-slate-500">{t('hero.rating')}</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 translate-y-4 scale-90 rounded-full bg-[#C6A87C]/20 blur-3xl" />
              <div className="relative rounded-2xl border border-white/60 bg-white p-8 shadow-[0_20px_25px_-5px_rgba(0,0,0,0.1),0_10px_10px_-5px_rgba(0,0,0,0.04)]">
                <h2 className="font-display text-2xl font-bold text-slate-900 mb-6">
                  {t('upload.title')}
                </h2>
                <div
                  {...getRootProps()}
                  className={`group relative cursor-pointer rounded-xl border-2 border-dashed p-10 text-center transition-all duration-300 ${
                    isDragActive
                      ? 'border-[#C6A87C] bg-[#C6A87C]/5'
                      : 'border-slate-300 hover:border-[#C6A87C] hover:bg-slate-50'
                  }`}
                >
                  <input {...getInputProps()} />
                  {uploadedFile ? (
                    <div className="space-y-4">
                      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                        <svg
                          className="h-8 w-8 text-emerald-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-lg font-medium text-slate-900">{uploadedFile.name}</p>
                        <p className="text-sm text-slate-500">
                          {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          setUploadedFile(null);
                        }}
                        className="text-sm font-medium text-[#C6A87C] hover:text-[#B09265]"
                      >
                        {t('upload.remove')}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 transition-transform duration-300 group-hover:scale-110">
                        <span className="text-3xl text-[#C6A87C]">☁️</span>
                      </div>
                      <div>
                        <p className="text-lg font-medium text-slate-900">
                          {isDragActive ? t('upload.drop') : t('upload.drag')}
                        </p>
                        <p className="text-sm text-slate-500">
                          <Trans
                            i18nKey="upload.browse"
                            ns="landing"
                            components={[
                              <span key="unused" />,
                              <span key="browse" className="font-medium text-[#C6A87C]" />,
                            ]}
                          />
                        </p>
                        <p className="mt-2 text-xs text-slate-400">{t('upload.support')}</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-6 flex gap-3 rounded-lg border border-emerald-100 bg-emerald-50 p-4">
                  <span className="text-xl text-emerald-600">🔒</span>
                  <div>
                    <h4 className="text-sm font-semibold text-emerald-800">{t('upload.secureTitle')}</h4>
                    <p className="mt-1 text-xs text-emerald-700">{t('upload.secureDesc')}</p>
                  </div>
                </div>
                {uploadedFile && (
                  <div className="mt-6">
                    <button
                      type="button"
                      onClick={handleStartDispute}
                      disabled={uploading}
                      className="flex w-full items-center justify-center rounded-lg bg-[#C6A87C] px-4 py-3 text-sm font-medium text-white transition-all duration-300 hover:bg-[#B09265] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {uploading ? <LoadingSpinner size="sm" color="white" /> : t('upload.continueWithTicket')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {delaying && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80">
          <div className="flex flex-col items-center space-y-3">
            <LoadingSpinner size="lg" color="primary" />
            <span className="text-sm text-slate-700">Preparing your upload…</span>
          </div>
        </div>
      )}

      <section className="border-t border-slate-100 bg-white py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4 lg:gap-12">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="mb-4 text-3xl">{stat.icon}</div>
                <div className="font-display text-3xl font-bold text-slate-900">{stat.value}</div>
                <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#FDFBF7] py-16">
        <div className="mx-auto max-w-6xl px-4 text-center">
          <h2 className="font-display text-3xl font-bold text-slate-900">
            Reviewed by AI + Human Experts
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Optional human review helps refine clarity and completeness before you submit.
          </p>
          <p className="mt-2 text-sm text-slate-500">
            Not a law firm. No legal advice.
          </p>
          <p className="mt-6 text-base text-slate-600">
            Received a parking ticket in Toronto and not sure what to do next? DisputeBot Toronto
            helps you dispute or appeal your parking ticket online with step-by-step guidance and
            AI-powered appeal letters—no legal knowledge required.
          </p>
        </div>
      </section>

      <section className="bg-[#F8F5EE] py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <h2 className="font-display text-3xl font-bold text-slate-900 lg:text-4xl">
              {t('how.title')}
            </h2>
            <p className="mt-4 text-lg text-slate-600">{t('how.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {howItWorksSteps.map((step, index) => (
              <div key={step.step} className="relative">
                <div className="rounded-xl border border-white/70 bg-white/80 p-6 shadow-sm">
                  <div className="text-center">
                    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#C6A87C]/10 text-2xl">
                      {step.icon}
                    </div>
                    <div className="mx-auto mb-3 flex h-8 w-8 items-center justify-center rounded-full bg-[#C6A87C] text-sm font-semibold text-white">
                      {step.step}
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">{step.title}</h3>
                    <p className="mt-2 text-sm text-slate-600">{step.description}</p>
                  </div>
                </div>
                {index < howItWorksSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 -translate-y-1/2 text-[#C6A87C]/60">
                    →
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/how-it-works"
              className="inline-flex items-center justify-center rounded-lg bg-[#C6A87C] px-8 py-3 text-sm font-medium text-white transition-all duration-300 hover:bg-[#B09265]"
            >
              {t('how.learnMore')}
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <h2 className="font-display text-3xl font-bold text-slate-900 lg:text-4xl">
              {t('testimonials.title')}
            </h2>
            <p className="mt-4 text-lg text-slate-600">{t('testimonials.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="rounded-xl border border-slate-200 bg-[#FDFBF7] p-6 shadow-sm">
                <div className="mb-4 flex items-center">
                  <div className="flex text-[#C6A87C]">{'★'.repeat(testimonial.rating)}</div>
                  <div className="ml-auto rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-800">
                    {t('testimonials.saved', { amount: testimonial.saved })}
                  </div>
                </div>
                <p className="text-sm text-slate-600">"{testimonial.text}"</p>
                <div className="mt-6">
                  <div className="font-semibold text-slate-900">{testimonial.name}</div>
                  <div className="text-xs text-slate-500">{testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F8F5EE] py-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-slate-900 md:text-3xl">
            How to Dispute a Parking Ticket in Toronto
          </h2>
          <ul className="mt-6 grid gap-4 text-sm text-slate-700">
            <li className="flex items-start gap-3">
              <span className="font-semibold text-[#C6A87C]">1.</span>
              <span>Upload your Toronto parking ticket (photo or PDF)</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="font-semibold text-[#C6A87C]">2.</span>
              <span>Answer a few guided questions about your situation</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="font-semibold text-[#C6A87C]">3.</span>
              <span>Get a personalized parking ticket appeal letter</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="font-semibold text-[#C6A87C]">4.</span>
              <span>Follow step-by-step instructions to submit your appeal</span>
            </li>
          </ul>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-slate-900 md:text-3xl">
            Frequently Asked Questions About Parking Ticket Appeals in Toronto
          </h2>
          <div className="mt-6 space-y-4">
            {faqItems.map((item) => (
              <div key={item.question} className="rounded-xl border border-slate-200 bg-[#FDFBF7] p-5">
                <h3 className="text-lg font-semibold text-slate-900">{item.question}</h3>
                <p className="mt-2 text-sm text-slate-700">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
        />
      </section>

      <section className="bg-[#1E293B] py-20">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-white lg:text-4xl">
            {t('cta.title')}
          </h2>
          <p className="mt-4 text-lg text-slate-200">{t('cta.subtitle')}</p>
          <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
            <button
              onClick={handleStartDispute}
              disabled={uploading}
              className="rounded-lg bg-[#C6A87C] px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#B09265] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {uploading ? <LoadingSpinner size="sm" color="white" /> : t('cta.primary')}
            </button>
            <Link
              to="/pricing"
              className="rounded-lg border border-white/70 px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-white hover:text-slate-900"
            >
              {t('cta.secondary')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
